import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import {
  FaPlane,
  FaShip,
  FaMapMarkerAlt,
  FaCheck,
  FaChevronLeft,
  FaClock,
  FaChevronRight,
} from 'react-icons/fa';
import { ScrollText, Reveal } from '../../../components/motion';
import Icon from '../../../components/ui/Icon';
import bruneiMap from '../../../assets/images/brunei-map.webp';
import bangladeshMap from '../../../assets/images/bangladesh-map.webp';
import indiaMap from '../../../assets/images/india-map.webp';
import philippinesMap from '../../../assets/images/philippines-map.webp';
import srilankaMap from '../../../assets/images/srilanka-map.webp';
import malaysiaMap from '../../../assets/images/malaysia-map.webp';
import singaporeMap from '../../../assets/images/singapore-map.webp';
import ukMap from '../../../assets/images/uk-map.webp';
import nepalMap from '../../../assets/images/nepal-map.webp';
import pakistanMap from '../../../assets/images/pakistan-map.webp';
import bruneiPattern from '../../../assets/images/brunei-pattern.webp';
import bangladeshPattern from '../../../assets/images/bangladesh-pattern.webp';
import indiaPattern from '../../../assets/images/india-pattern.webp';
import philippinesPattern from '../../../assets/images/philippines-pattern.webp';
import srilankaPattern from '../../../assets/images/srilanka-pattern.webp';
import malaysiaPattern from '../../../assets/images/malaysia-pattern.webp';
import singaporePattern from '../../../assets/images/singapore-pattern.webp';
import ukPattern from '../../../assets/images/uk-pattern.webp';
import nepalPattern from '../../../assets/images/nepal-pattern.webp';
import pakistanPattern from '../../../assets/images/pakistan-pattern.webp';
// Square flags from flag-icons (MIT), cropped to circles in the tabs.
import bruneiFlag from '../../../assets/images/flags/bn.svg';
import bangladeshFlag from '../../../assets/images/flags/bd.svg';
import indiaFlag from '../../../assets/images/flags/in.svg';
import philippinesFlag from '../../../assets/images/flags/ph.svg';
import srilankaFlag from '../../../assets/images/flags/lk.svg';
import malaysiaFlag from '../../../assets/images/flags/my.svg';
import singaporeFlag from '../../../assets/images/flags/sg.svg';
import ukFlag from '../../../assets/images/flags/gb.svg';
import nepalFlag from '../../../assets/images/flags/np.svg';
import pakistanFlag from '../../../assets/images/flags/pk.svg';
import Eyebrow from '../../../components/ui/Eyebrow';
import styles from './ShippingDestinationsAccordion.module.css';

type Destination = {
  code: string; // ISO-2
  name: string;
  /** Square flag, shown as a round badge on the tab. */
  flag: string;
  description: string;
  highlights: string[];
  /** Air cargo only — no sea freight to this country (Malaysia, Brunei). */
  airOnly?: boolean;
  /** Flag-filled country map graphic (shown in the expanded card). */
  map?: string;
  /** Subtle facet pattern overlaid on the red card background. */
  pattern?: string;
};

const DESTINATIONS: Destination[] = [
  {
    code: 'bn',
    flag: bruneiFlag,
    map: bruneiMap,
    pattern: bruneiPattern,
    name: 'Brunei',
    description:
      "Send cargo from Saudi Arabia to Brunei by air cargo, door to door. We collect from your address anywhere we serve in the Kingdom, clear customs at both ends, and deliver to the recipient's door in Brunei — with every box tracked along the way.",
    airOnly: true,
    highlights: [
      'Door-to-Door Delivery',
      'Air Cargo',
      'Pickup Across Saudi Arabia',
      'Professional Packing',
      'Customs Cleared by Us',
      'Every Box Tracked',
    ],
  },
  {
    code: 'bd',
    flag: bangladeshFlag,
    map: bangladeshMap,
    pattern: bangladeshPattern,
    name: 'Bangladesh',
    description:
      "Send cargo from Saudi Arabia to Bangladesh — gifts for family, personal belongings, or business goods. We collect from your address, pack and number every box, clear customs in Saudi Arabia and Bangladesh, and deliver to the recipient's door by air or sea.",
    highlights: [
      'Door-to-Door Delivery',
      'Air Cargo & Sea Freight',
      'Pickup Across Saudi Arabia',
      'Professional Packing',
      'Customs Cleared by Us',
      'Every Box Tracked',
    ],
  },
  {
    code: 'in',
    flag: indiaFlag,
    map: indiaMap,
    pattern: indiaPattern,
    name: 'India',
    description:
      "Send cargo from Saudi Arabia to India door to door, by air cargo or sea freight. We collect from your home or office, handle export and Indian import customs, and deliver to the recipient's address — with tracking from pickup to delivery.",
    highlights: [
      'Door-to-Door Delivery',
      'Air Cargo & Sea Freight',
      'Pickup Across Saudi Arabia',
      'Professional Packing',
      'Customs Cleared by Us',
      'Every Box Tracked',
    ],
  },
  {
    code: 'ph',
    flag: philippinesFlag,
    map: philippinesMap,
    pattern: philippinesPattern,
    name: 'Philippines',
    description:
      "Send cargo from Saudi Arabia to the Philippines — balikbayan-style boxes, personal items, and business shipments. We pick up from your address, clear customs at both ends, and deliver door to door by air or sea, with every box tracked.",
    highlights: [
      'Door-to-Door Delivery',
      'Air Cargo & Sea Freight',
      'Pickup Across Saudi Arabia',
      'Professional Packing',
      'Customs Cleared by Us',
      'Every Box Tracked',
    ],
  },
  {
    code: 'lk',
    flag: srilankaFlag,
    map: srilankaMap,
    pattern: srilankaPattern,
    name: 'Sri Lanka',
    description:
      "Send cargo from Saudi Arabia to Sri Lanka by air or sea. We collect from your address, pack and label each box, handle customs in Saudi Arabia and Sri Lanka, and deliver straight to the recipient's door.",
    highlights: [
      'Door-to-Door Delivery',
      'Air Cargo & Sea Freight',
      'Pickup Across Saudi Arabia',
      'Professional Packing',
      'Customs Cleared by Us',
      'Every Box Tracked',
    ],
  },
  {
    code: 'my',
    flag: malaysiaFlag,
    map: malaysiaMap,
    pattern: malaysiaPattern,
    name: 'Malaysia',
    description:
      "Send cargo from Saudi Arabia to Malaysia door to door. Personal belongings, gifts, or commercial goods — we collect, clear customs at both ends, and deliver to the recipient's address by air cargo, tracked all the way.",
    airOnly: true,
    highlights: [
      'Door-to-Door Delivery',
      'Air Cargo',
      'Pickup Across Saudi Arabia',
      'Professional Packing',
      'Customs Cleared by Us',
      'Every Box Tracked',
    ],
  },
  {
    code: 'sg',
    flag: singaporeFlag,
    map: singaporeMap,
    pattern: singaporePattern,
    name: 'Singapore',
    description:
      "Send cargo from Saudi Arabia to Singapore door to door. We collect from your address in the Kingdom, handle export and Singapore import customs, and deliver to the recipient's door by air or sea — with every box tracked.",
    highlights: [
      'Door-to-Door Delivery',
      'Air Cargo & Sea Freight',
      'Pickup Across Saudi Arabia',
      'Professional Packing',
      'Customs Cleared by Us',
      'Every Box Tracked',
    ],
  },
  {
    code: 'gb',
    flag: ukFlag,
    map: ukMap,
    pattern: ukPattern,
    name: 'United Kingdom',
    description:
      "Send cargo from Saudi Arabia to the United Kingdom door to door, by air or sea. We collect from your home or office, clear customs in Saudi Arabia and the UK, and deliver to the recipient's address anywhere in the UK, tracked from pickup to delivery.",
    highlights: [
      'Door-to-Door Delivery',
      'Air Cargo & Sea Freight',
      'Pickup Across Saudi Arabia',
      'Professional Packing',
      'Customs Cleared by Us',
      'Every Box Tracked',
    ],
  },
  {
    code: 'np',
    flag: nepalFlag,
    map: nepalMap,
    pattern: nepalPattern,
    name: 'Nepal',
    description:
      "Send cargo from Saudi Arabia to Nepal — gifts for family, personal belongings, or business goods. We collect from your address, pack and number every box, handle customs at both ends, and deliver to the recipient's door.",
    highlights: [
      'Door-to-Door Delivery',
      'Air Cargo & Sea Freight',
      'Pickup Across Saudi Arabia',
      'Professional Packing',
      'Customs Cleared by Us',
      'Every Box Tracked',
    ],
  },
  {
    code: 'pk',
    flag: pakistanFlag,
    map: pakistanMap,
    pattern: pakistanPattern,
    name: 'Pakistan',
    description:
      "Send cargo from Saudi Arabia to Pakistan door to door, by air or sea. We collect from your home or office, pack and number every box, clear customs in Saudi Arabia and Pakistan, and deliver to the recipient's address — tracked all the way.",
    highlights: [
      'Door-to-Door Delivery',
      'Air Cargo & Sea Freight',
      'Pickup Across Saudi Arabia',
      'Professional Packing',
      'Customs Cleared by Us',
      'Every Box Tracked',
    ],
  },
];

/** Typical door-to-door time (the owner quotes 10–15 days by air or sea),
    how it ships (Malaysia and Brunei are air only), and pickup. */
const quickFacts = (d: Destination) => [
  { icon: FaClock, label: 'Transit time', value: '10–15 days' },
  d.airOnly
    ? { icon: FaPlane, label: 'Shipping', value: 'Air cargo only' }
    : { icon: FaShip, label: 'Shipping', value: 'Air & sea' },
  { icon: FaMapMarkerAlt, label: 'Pickup', value: '12 branches in KSA' },
];

/** Must match the .panel transition in the CSS. */
const OPEN_MS = 550;
/** How long the pointer rests on a tab before it opens (stops flicker when
    the mouse just passes across the row). */
const HOVER_MS = 120;

const ShippingDestinationsAccordion: React.FC = () => {
  const [active, setActive] = useState(0);
  const [canScroll, setCanScroll] = useState({ left: false, right: false });
  const rowRef = useRef<HTMLDivElement>(null);
  const panelRefs = useRef<(HTMLDivElement | null)[]>([]);
  const hoverTimer = useRef<number | undefined>(undefined);
  // True while the row is sliding itself into place. Moving content under a
  // still pointer must not count as hovering a new country, or one opening
  // would chain-open its neighbours.
  const autoScrolling = useRef(false);

  // The row is wider than the screen on desktop, so it scrolls sideways; the
  // arrows (and edge fades) only show on the side that has more to see.
  const updateArrows = useCallback(() => {
    const row = rowRef.current;
    if (!row) return;
    const max = row.scrollWidth - row.clientWidth;
    setCanScroll({ left: row.scrollLeft > 4, right: row.scrollLeft < max - 4 });
  }, []);

  useEffect(() => {
    updateArrows();
    window.addEventListener('resize', updateArrows);
    return () => window.removeEventListener('resize', updateArrows);
  }, [updateArrows]);

  // Once the chosen card has finished opening, and only if part of it is
  // off-screen, slide the row so the whole card is in view. On the stacked
  // phone layout the row doesn't scroll sideways, so this does nothing.
  useEffect(() => {
    const timer = window.setTimeout(() => {
      const row = rowRef.current;
      const panel = panelRefs.current[active];
      if (!row || !panel || row.scrollWidth <= row.clientWidth) return;
      const start = panel.offsetLeft;
      const end = start + panel.offsetWidth;
      const view = row.scrollLeft;
      if (start >= view && end <= view + row.clientWidth) return;
      autoScrolling.current = true;
      const left = start - (row.clientWidth - panel.offsetWidth) / 2;
      row.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
    }, OPEN_MS);
    return () => window.clearTimeout(timer);
  }, [active]);

  useEffect(() => () => window.clearTimeout(hoverTimer.current), []);

  // Hovering a tab opens it after a short rest. Only real pointer movement
  // counts: browsers also send mouse events when content scrolls under a
  // resting pointer (movement 0), and those are ignored.
  const hoverOpen = (i: number, e: React.MouseEvent) => {
    if (!e.movementX && !e.movementY) return;
    autoScrolling.current = false;
    if (i === active) return;
    window.clearTimeout(hoverTimer.current);
    hoverTimer.current = window.setTimeout(() => {
      if (!autoScrolling.current) setActive(i);
    }, HOVER_MS);
  };

  const scrollByPage = (dir: 1 | -1) => {
    const row = rowRef.current;
    if (row) row.scrollBy({ left: dir * row.clientWidth * 0.6, behavior: 'smooth' });
  };

  return (
    <section className={styles.section}>
      <Container fluid="xl" className={styles.inner}>
        <div className={styles.eyebrowRow}>
          <Eyebrow>Where we ship</Eyebrow>
        </div>
        <ScrollText as="h2" className={styles.title}>
          Our Door To Door Cargo Destinations
        </ScrollText>
        <Reveal as="p" className={styles.subtitle} delay={0.05}>
          Door-to-door cargo from Saudi Arabia to 10 countries
        </Reveal>
      </Container>

      {/* Phones and tablets: a swipeable row of flag chips picks the country;
          only that country's card shows below. Hidden on desktop. */}
      <div className={styles.chipBar}>
        {DESTINATIONS.map((d, i) => (
          <button
            key={d.code}
            type="button"
            className={`${styles.chip} ${i === active ? styles.chipOn : ''}`}
            onClick={() => setActive(i)}
            aria-pressed={i === active}
          >
            <img src={d.flag} alt="" className={styles.chipFlag} />
            {d.name}
          </button>
        ))}
      </div>

      {/* Wider than the text column above, so the tabs and card get room. */}
      <div className={styles.rowWrap}>
        {canScroll.left && (
          <button
            type="button"
            className={`${styles.arrow} ${styles.arrowLeft}`}
            onClick={() => scrollByPage(-1)}
            aria-label="Show previous countries"
          >
            <Icon icon={FaChevronLeft} aria-hidden="true" />
          </button>
        )}

        <div
          ref={rowRef}
          className={`${styles.row} ${canScroll.left ? styles.fadeLeft : ''} ${
            canScroll.right ? styles.fadeRight : ''
          }`}
          onScroll={updateArrows}
        >
          {DESTINATIONS.map((d, i) => {
            const isActive = i === active;
            return (
              <div
                key={d.code}
                ref={(el) => {
                  panelRefs.current[i] = el;
                }}
                className={`${styles.panel} ${isActive ? styles.active : ''}`}
                onMouseMove={(e) => hoverOpen(i, e)}
                onMouseLeave={() => window.clearTimeout(hoverTimer.current)}
              >
                {/* Closed state: a tall white tab. It fades out as its card
                    opens over it. */}
                <button
                  type="button"
                  className={styles.tab}
                  onClick={() => setActive(i)}
                  aria-expanded={isActive}
                  aria-controls={`dest-${d.code}`}
                  tabIndex={isActive ? -1 : 0}
                >
                  <img className={styles.tabFlag} src={d.flag} alt="" />
                  <span className={styles.vName}>{d.name}</span>
                  {d.map && (
                    <img className={styles.tabMap} src={d.map} alt="" loading="lazy" />
                  )}
                  <span className={styles.tabMode}>{d.airOnly ? 'Air' : 'Air · Sea'}</span>
                  <span className={styles.tabPlus} aria-hidden="true">
                    +
                  </span>
                </button>

                {/* Open state: the red card. Inert while closed so its links
                    can't take keyboard focus. */}
                <div
                  id={`dest-${d.code}`}
                  className={styles.card}
                  inert={!isActive}
                  style={
                    {
                      '--card-pattern': d.pattern ? `url(${d.pattern})` : 'none',
                    } as React.CSSProperties
                  }
                >
                  {/* Fixed full card width inside a card that grows with the
                      panel, so the text never re-wraps while it opens and a
                      closed card never sticks out past its tab. */}
                  <div className={styles.cardInner}>
                    <div className={styles.cardMain}>
                      <div className={styles.copy}>
                        <h3 className={styles.destName}>
                          <img src={d.flag} alt="" className={styles.destFlag} />
                          {d.name}
                        </h3>
                        <p className={styles.destDesc}>{d.description}</p>
                        <ul className={styles.hlList}>
                          {d.highlights.map((h) => (
                            <li key={h} className={styles.hlItem}>
                              <Icon icon={FaCheck} className={styles.hlCheck} aria-hidden="true" />
                              {h}
                            </li>
                          ))}
                        </ul>
                        <div className={styles.ctas}>
                          <Link to="/book-shipment" className={styles.ctaPrimary}>
                            Get a quote for {d.name}
                          </Link>
                          <Link to="/tracking" className={styles.ctaGhost}>
                            Track shipment
                          </Link>
                        </div>
                      </div>
                      <img
                        className={styles.destMap}
                        src={d.map ?? d.flag}
                        alt={`${d.name} map`}
                        loading="lazy"
                      />
                    </div>

                    <ul className={styles.facts}>
                      {quickFacts(d).map((f) => (
                        <li key={f.label} className={styles.fact}>
                          <span className={styles.factIcon} aria-hidden="true">
                            <Icon icon={f.icon} />
                          </span>
                          <span>
                            <small className={styles.factLabel}>{f.label}</small>
                            <strong className={styles.factValue}>{f.value}</strong>
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {canScroll.right && (
          <button
            type="button"
            className={`${styles.arrow} ${styles.arrowRight}`}
            onClick={() => scrollByPage(1)}
            aria-label="Show more countries"
          >
            <Icon icon={FaChevronRight} aria-hidden="true" />
          </button>
        )}
      </div>

      {/* Desktop: a dot per country, the open one long and red. */}
      <div className={styles.position} aria-hidden="true">
        <span className={styles.dots}>
          {DESTINATIONS.map((d, i) => (
            <i key={d.code} className={i === active ? styles.dotOn : ''} />
          ))}
        </span>
      </div>
    </section>
  );
};

export default ShippingDestinationsAccordion;
